import React, { useRef, useState, useEffect } from "react"
import { DataGrid, Button, Template, Form, LoadPanel } from "devextreme-react"
import {
  Column,
  Button as GButton,
  Editing,
  StateStoring,
  SearchPanel,
  ColumnChooser,
  ColumnFixing,
  FilterPanel,
  Grouping,
  Pager,
  HeaderFilter,
  FilterRow,
  Scrolling,
} from "devextreme-react/data-grid"
import DataSource from "devextreme/data/data_source"
import downloadJs from "downloadjs"
import { Item } from "devextreme-react/form"
import { connect } from "react-redux"
import notify from "devextreme/ui/notify"
import { FaFile } from "react-icons/fa"

import { batchJobDataStoreGenerator } from "../shared/ConcurStore"
import { batchDataStore } from "../shared/ConcurStore"
import {
  closeBatches,
  downloadBatchJobFile,
  fetchBatchJob,
  fetchBatchJobs,
  processBatchJob,
  processBatchJobs,
  updateBatchJobs,
} from "../operations/concur"
import { FormW, PageTitle } from "../shared/StyledComponents"
import { hasAccessTo } from "../../authentication"
import { dashboardSelectors } from "../../../state/ducks/dashboard"
import ItemScheduler from "../shared/ItemScheduler"
import useResponsiveWidth from "../shared/hooks/useResponsiveWidth"
import SEO from "../../seo"
import { faCloudDownloadAlt, grRefresh, vscNewFile } from "../shared/icons"

const BatchJobs = ({ batchId, refreshUuid }) => {
  const formW = useRef(null)
  const batchForm = useRef(null)
  const jobsGrid = useRef(null)
  const concurJobDataSourceRef = useRef(
    new DataSource({
      store: batchJobDataStoreGenerator(batchId),
    })
  )
  const [isLoading, setIsLoading] = useState(false)
  const { md, lg } = useResponsiveWidth()

  useEffect(() => {
    jobsGrid.current.instance.refresh(true)
  }, [refreshUuid])

  const concurJobDataSource = concurJobDataSourceRef.current
  useEffect(() => {
    const getBatchInfo = async () => {
      setIsLoading(true)
      try {
        let response = await batchDataStore.byKey(batchId)
        batchForm.current.instance.option("formData", response.data)
      } finally {
        setIsLoading(false)
      }
    }
    getBatchInfo()
  }, [batchId])

  const refreshJobGridClickHandler = () => {
    jobsGrid.current.instance.refresh(true)
  }
  const updateJobClickHandler = async () => {
    try {
      await updateBatchJobs(batchId)
      notify("Updating batch jobs was queued successfully.", "success")
    } catch (error) {
      notify(`Updating batch jobs failed: ${error.message}`, "error")
    }
  }
  const requestNewBatchClickHandler = async () => {
    try {
      await closeBatches(batchId)
      notify("Closing batch job was queued successfully.", "success")
    } catch (error) {
      notify(`Closing batch job failed: ${error.message}`, "error")
    }
  }
  const jobButtonDownloadClickHandler = async e => {
    const jobId = e.row.data.id
    try {
      const file = await downloadBatchJobFile(batchId, jobId)
      let fileExtension
      switch (file.fileType) {
        case "application/zip":
          fileExtension = ".zip"
          break
        case "text/csv":
          fileExtension = ".csv"
          break
        default:
          fileExtension = ""
      }
      downloadJs(
        atob(file.data),
        `batch-${batchId}-job-${jobId}${fileExtension}`,
        `${file.fileType}`
      )
    } catch (error) {
      notify(`Downloading batch job file failed: ${error.message}`, "error")
    }
  }
  const jobButtonFetchClickHandler = async e => {
    const jobId = e.row.data.id
    try {
      await fetchBatchJob(batchId, jobId)
      notify("Fetch batch job file was queued successfully.", "success")
    } catch (error) {
      notify(`Fetching batch job file failed: ${error.message}`, "error")
    }
  }
  const fetchBatchesClickHandler = async () => {
    try {
      await fetchBatchJobs(batchId)
      notify("Fetch batch job files was queued successfully.", "success")
    } catch (error) {
      notify(`Fetching batch job files failed: ${error.message}`, "error")
    }
  }
  const jobButtonProcessClickHandler = async e => {
    const jobId = e.row.data.id
    try {
      await processBatchJob(batchId, jobId)
      notify("Process batch job file was queued successfully.", "success")
    } catch (error) {
      notify(`Processing batch job file failed: ${error.message}`, "error")
    }
  }
  const processBatchesClickHandler = async () => {
    try {
      await processBatchJobs(batchId)
      notify("Process batch job files was queued successfully.", "success")
    } catch (error) {
      notify(`Process batch job files failed: ${error.message}`, "error")
    }
  }

  return (
    <>
      <SEO title="Concur Batch Jobs" />
      <FormW ref={formW}>
        <Form
          readOnly={true}
          ref={batchForm}
          visible={hasAccessTo("concur.batch.r")}
        >
          <Item itemType="group" caption="Batch information" colCount={2}>
            <Item dataField="label" />
            <Item dataField="concur_BatchName" />
            <Item dataField="concur_BatchId" />
            <Item dataField="isActive" editorType="dxCheckBox" />
          </Item>
        </Form>
        <LoadPanel
          visible={isLoading}
          container={formW.current}
          position={{
            of: formW.current,
          }}
        />
      </FormW>
      <PageTitle icon={FaFile} variant="subTitle">
        Jobs
      </PageTitle>
      <DataGrid
        allowColumnReordering={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
        columnResizingMode="widget"
        dataSource={concurJobDataSource}
        hoverStateEnabled={true}
        ref={jobsGrid}
        rowAlternationEnabled={true}
        showBorders={true}
        visible={hasAccessTo("concur.batch.job.r")}
        onToolbarPreparing={e => {
          e.toolbarOptions.items.unshift({
            location: "before",
            template: "processBatches",
          })
          e.toolbarOptions.items.unshift({
            location: "before",
            template: "fetchBatches",
          })
          e.toolbarOptions.items.unshift({
            location: "before",
            template: "updateBatches",
          })
          e.toolbarOptions.items.unshift({
            location: "before",
            template: "requestNewBatch",
          })
          e.toolbarOptions.items.unshift({
            location: "before",
            template: "refreshGrid",
          })
        }}
      >
        <Column dataField="batchId" dataType="number" defaultVisible={false} />
        <Column
          dataField="concur_BatchId"
          dataType="number"
          defaultVisible={false}
        />
        <Column dataField="concur_BatchName" dataType="string" />
        <Column dataField="concur_BatchTotal" dataType="number" />
        <Column dataField="concur_Currency" dataType="string" />
        <Column dataField="concur_Count" dataType="number" />
        <Column dataField="concur_Type" dataType="string" />
        <Column dataField="concur_PaymentMethod" dataType="string" />
        <Column dataField="concur_UrlId" dataType="string" />
        <Column dataField="concur_BatchStatus" dataType="string" />
        <Column dataField="concur_Status" dataType="string" />
        <Column dataField="concur_FileUrlId" dataType="string" />
        <Column type="buttons" showInColumnChooser={false}>
          <GButton
            name="download"
            hint="Download job file."
            icon="file"
            visible={hasAccessTo("concur.batch.job.file")}
            onClick={jobButtonDownloadClickHandler}
          />
          <GButton
            name="fetch"
            hint="Fetch job file from Concur."
            icon="download"
            visible={hasAccessTo("concur.batch.job.fetch")}
            onClick={jobButtonFetchClickHandler}
          />
          <GButton
            name="process"
            hint="Process job file."
            icon="refresh"
            visible={hasAccessTo("concur.batch.job.process")}
            onClick={jobButtonProcessClickHandler}
          />
        </Column>
        <ColumnChooser
          allowSearch={true}
          enabled={true}
          height="400"
          mode="select"
        />
        <ColumnFixing enabled={true} />
        <Editing mode="row" useIcons={true} allowDeleting={true} />
        <FilterPanel visible={true} />
        <FilterRow visible={true} />
        <Grouping contextMenuEnabled={true} />
        <HeaderFilter visible={true} />
        <Pager
          visible={true}
          allowedPageSizes={[10, 20, 50, 100]}
          showPageSizeSelector={true}
          showNavigationButtons={true}
          showInfo={true}
        />
        <Scrolling showScrollbar="always" />
        <SearchPanel width={250} visible={md || lg} />
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey="concur-batch-jobs"
        />
        <Template name="refreshGrid">
          <Button icon="refresh" onClick={refreshJobGridClickHandler} />
        </Template>
        <Template name="requestNewBatch">
          <Button
            elementAttr={{ id: "requestNewBatch" }}
            icon={vscNewFile}
            text="Request New Batch"
            visible={hasAccessTo("concur.batch.close")}
            onClick={requestNewBatchClickHandler}
          />
        </Template>
        <Template name="updateBatches">
          <Button
            elementAttr={{ id: "updateBatches" }}
            icon={grRefresh}
            text="Update Batches"
            visible={hasAccessTo("concur.batch.status")}
            onClick={updateJobClickHandler}
          />
        </Template>
        <Template name="fetchBatches">
          <Button
            elementAttr={{ id: "fetchBatches" }}
            icon={faCloudDownloadAlt}
            text="Fetch Batches"
            visible={hasAccessTo("concur.batch.job.fetch")}
            onClick={fetchBatchesClickHandler}
          />
        </Template>
        <Template name="processBatches">
          <Button
            elementAttr={{ id: "processBatches" }}
            icon="refresh"
            text="Process Batches"
            visible={hasAccessTo("concur.batch.job.process")}
            onClick={processBatchesClickHandler}
          />
        </Template>
      </DataGrid>
      <ItemScheduler
        items={[
          {
            body: null,
            method: "POST",
            target: "#requestNewBatch",
            title: `request new batch (batchId=${batchId})`,
            url: `/api/v1/concur/batches/${batchId}/close`,
          },
          {
            body: null,
            method: "POST",
            target: "#updateBatches",
            title: `update batches (batchId=${batchId})`,
            url: `/api/v1/concur/batches/${batchId}/update-jobs`,
          },
          {
            body: null,
            method: "POST",
            target: "#fetchBatches",
            title: `fetch batches (batchId=${batchId})`,
            url: `/api/v1/concur/batches/${batchId}/jobs/fetch`,
          },
          {
            body: null,
            method: "POST",
            target: "#processBatches",
            title: `process batches (batchId=${batchId})`,
            url: `/api/v1/concur/batches/${batchId}/jobs/process`,
          },
        ]}
      />
    </>
  )
}

export { BatchJobs }

const mapStateToProps = state => ({
  timeRange: dashboardSelectors.selectTimeRange(state),
  refreshUuid: dashboardSelectors.selectAutoRefreshUuid(state),
})
export default connect(mapStateToProps, null)(BatchJobs)
